import {
  BOGGLE_BADGE_DEFINITIONS,
  getBoggleBadgeAuthoredOrder,
} from '~/boggle/config/badges/registry'
import { buildTierBadgeId, isTieredBadgeId } from '~/boggle/config/badges/familyBadgeId'
import type { BoggleBadgeFamilyId } from '~/boggle/config/badges/familyTypes'
import type { BoggleBadgeId } from '~/boggle/config/badges/types'
import { BOGGLE_NON_TIERED_BADGE_IDS } from '~/boggle/config/badges/nonTieredBadgeIds'
import type { BoggleRunLedger } from '~/boggle/engine/standard/types'
import { BOGGLE_BADGE_CHECKS } from './checks'
import {
  BOGGLE_TIERED_FAMILY_IDS,
  getHighestTieredFamilyTierAtIndex,
} from './familyEvaluators'

/**
 * Earned-badge record in the deterministic order the run produced them.
 * `earnedAtMs` is in run-time (not wall clock) so replays match.
 */
export type BoggleEarnedBadge = {
  badgeId: BoggleBadgeId
  earnedAtLedgerIndex: number
  earnedAtMs: number
}

/**
 * Walks the run ledger once and returns the badges that became earned, in the
 * stable order they were earned. When multiple badges become earned at the
 * same ledger entry, the authored order in
 * `~/boggle/config/badges/registry.ts` is used as a tie-break.
 *
 * Tiered families emit only the highest tier reached (replacing lower tiers
 * when a higher tier is earned later in the run).
 */
export function evaluateBoggleRunBadges(
  ledger: BoggleRunLedger,
): readonly BoggleEarnedBadge[] {
  const earnedFlat = new Set<BoggleBadgeId>()
  const earnedFamilyTier = new Map<BoggleBadgeFamilyId, number>()
  const results: BoggleEarnedBadge[] = []

  for (let index = 0; index < ledger.length; index++) {
    const entry = ledger[index]
    if (!entry) {
      continue
    }
    const newlyEarned: BoggleBadgeId[] = []

    for (const badgeId of BOGGLE_NON_TIERED_BADGE_IDS) {
      if (earnedFlat.has(badgeId)) {
        continue
      }
      const check = BOGGLE_BADGE_CHECKS[badgeId]
      if (check(ledger, index)) {
        newlyEarned.push(badgeId)
      }
    }

    for (const familyId of BOGGLE_TIERED_FAMILY_IDS) {
      const tierMet = getHighestTieredFamilyTierAtIndex(familyId, ledger, index)
      if (tierMet === 0) {
        continue
      }
      const previousTier = earnedFamilyTier.get(familyId) ?? 0
      if (tierMet <= previousTier) {
        continue
      }
      const previousBadgeId =
        previousTier > 0 ? buildTierBadgeId(familyId, previousTier) : null
      if (previousBadgeId) {
        const removeIndex = results.findIndex(r => r.badgeId === previousBadgeId)
        if (removeIndex >= 0) {
          results.splice(removeIndex, 1)
        }
      }
      earnedFamilyTier.set(familyId, tierMet)
      newlyEarned.push(buildTierBadgeId(familyId, tierMet))
    }

    newlyEarned.sort(
      (a, b) => getBoggleBadgeAuthoredOrder(a) - getBoggleBadgeAuthoredOrder(b),
    )
    for (const badgeId of newlyEarned) {
      if (!isTieredBadgeId(badgeId)) {
        earnedFlat.add(badgeId)
      }
      results.push({
        badgeId,
        earnedAtLedgerIndex: index,
        earnedAtMs: entry.atMs,
      })
    }
  }

  return results
}
